import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { typedMemo } from '@utils';
import React, { useState } from 'react';
import {
  ToolbarButtonItem,
  ToolbarItem,
  ToolbarMoreOptionsItem,
  ToolbarProps,
  ToolbarTitleItem,
} from './types';

const ToolbarButton = typedMemo(
  ({ item }: Readonly<{ item: ToolbarButtonItem }>) => {
    if (!item.label && item.icon) {
      return (
        <IconButton
          onClick={item.onClick}
          disabled={item.disabled}
          color={item.color}
          size={item.size || 'medium'}
        >
          {item.icon}
        </IconButton>
      );
    }
    return (
      <Button
        variant={item.variant || 'outlined'}
        startIcon={item.icon}
        onClick={item.onClick}
        disabled={item.disabled}
        color={item.color}
        sx={{ minWidth: 'max-content' }}
      >
        {item.label}
      </Button>
    );
  },
);

const ToolbarMoreOptions = typedMemo(
  ({ item }: Readonly<{ item: ToolbarMoreOptionsItem }>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (menuItem: ToolbarButtonItem) => {
      menuItem.onClick();
      handleClose();
    };

    return (
      <>
        <IconButton
          onClick={handleClick}
          disabled={item.disabled}
          size="medium"
        >
          <MoreHoriz />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {item.children.map((childItem, childIndex) => {
            if (childItem.type === 'button') {
              const childKey = `menu-${childItem.label || 'item'}-${childIndex}`;
              return (
                <MenuItem
                  key={childKey}
                  onClick={() => handleMenuItemClick(childItem)}
                  disabled={childItem.disabled}
                >
                  {childItem.icon && (
                    <ListItemIcon>{childItem.icon}</ListItemIcon>
                  )}
                  <ListItemText>{childItem.label}</ListItemText>
                </MenuItem>
              );
            }
            return null;
          })}
        </Menu>
      </>
    );
  },
);

const ToolbarTitle = typedMemo(
  ({ item }: Readonly<{ item: ToolbarTitleItem }>) => {
    return <Typography variant="h6">{item.title}</Typography>;
  },
);

export const Toolbar = typedMemo(
  ({ items, className }: Readonly<ToolbarProps>) => {
    const arrangeItems = (items: ToolbarItem[]) => {
      const withoutIndex = items.filter((i) => i.index == null);
      const withIndex = items
        .filter((i) => i.index != null)
        .sort((a, b) => a.index! - b.index!);

      const result = [...withoutIndex];
      for (const it of withIndex) {
        const idx = Math.max(0, Math.min(it.index!, result.length));
        result.splice(idx, 0, it);
      }
      return result;
    };

    const leftItems = arrangeItems(items.filter((i) => i.align === 'left'));
    const rightItems = arrangeItems(items.filter((i) => i.align !== 'left'));

    return (
      <Stack
        direction="row"
        alignItems="center"
        paddingY="0.5rem"
        spacing="0.5rem"
        useFlexGap
        className={className}
      >
        {/* Linke Items */}
        {leftItems.map((item, index) => {
          let keyIdentifier = item.type;
          if (item.type === 'title') keyIdentifier += `-${item.title}`;
          if (item.type === 'button')
            keyIdentifier += `-${item.label || 'icon'}`;
          const key = `left-${keyIdentifier}-${index}`;

          switch (item.type) {
            case 'button':
              return <ToolbarButton key={key} item={item} />;
            case 'more-options':
              return <ToolbarMoreOptions key={key} item={item} />;
            case 'title':
              return <ToolbarTitle key={key} item={item} />;
            default:
              return null;
          }
        })}

        {/* Spacer für automatische Trennung zwischen links und rechts */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Rechte Items */}
        {rightItems.map((item, index) => {
          let keyIdentifier = item.type;
          if (item.type === 'title') keyIdentifier += `-${item.title}`;
          if (item.type === 'button')
            keyIdentifier += `-${item.label || 'icon'}`;
          const key = `right-${keyIdentifier}-${index}`;

          switch (item.type) {
            case 'button':
              return <ToolbarButton key={key} item={item} />;
            case 'more-options':
              return <ToolbarMoreOptions key={key} item={item} />;
            case 'title':
              return <ToolbarTitle key={key} item={item} />;
            default:
              return null;
          }
        })}
      </Stack>
    );
  },
);
